/**
 * @render react
 * @name Pagination
 * @example
 * <Pagination previousLabel="Go Back to" previousTitle="Verdict" previousUrl="/back-to" previousUrl="icon-previous" nextLabel="Continue Reading" nextTitle="MPG, CO2 and running costs" nextUrl="/next-to" nextIcon="icon-next" />
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Icon } from 'Components/atoms/Icon';
import { Link } from 'Components/molecules/Link';

import './_style.scss';

/**
 * Pagination component
 * @param {string} nextLabel - label for Next link
 * @param {string} nextTitle - title for Next link
 * @param {string} nextUrl - url for Next link
 * @param {string} nextIcon - icon to render
 * @param {string} previousLabel - label for Previous link
 * @param {string} previousTitle - title for Previous link
 * @param {string} previousUrl - url for Previous link
 * @param {string} previousIcon - icon to render
 * @param {string} layout - Layout of the component (default "default")
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra classes to be added to the component
 * @param {Object} context - Extra information coming from Polaris provider
 */
const Pagination = (
  {
    layout,
    nextLabel,
    nextTitle,
    nextUrl,
    nextIcon,
    previousLabel,
    previousTitle,
    previousUrl,
    previousIcon,
    loadStyles,
    context: { cssPrefix },
    extraClassNames,
  },
) => (
  <div className={classNames({
    [`${cssPrefix}__pagination`]: loadStyles,
    [`-${layout}`]: true,
    ...extraClassNames,
  })}
  >
    <div className={`${cssPrefix}__pagination--previous`}>
      {previousUrl && (
        <Link href={previousUrl}>
          <span className={`${cssPrefix}__pagination--previous-icon ${cssPrefix}__pagination--icon`}>
            {previousIcon ? <Icon icon={`${cssPrefix}__ico-${previousIcon}`} /> : <span>&#60;</span>}
          </span>
          {previousLabel && (
            <span className={`${cssPrefix}__pagination--previous-label ${cssPrefix}__pagination--label`}>
              {previousLabel}
            </span>
          )}
          {previousTitle && (
            <span className={`${cssPrefix}__pagination--previous-title ${cssPrefix}__pagination--title`}>
              {previousTitle}
            </span>
          )}
        </Link>
      )}
    </div>

    <div className={`${cssPrefix}__pagination--next`}>
      {nextUrl && (
        <Link href={nextUrl}>
          {nextLabel && (
            <span className={`${cssPrefix}__pagination--next-label ${cssPrefix}__pagination--label`}>
              {nextLabel}
            </span>
          )}
          {nextTitle && (
            <span className={`${cssPrefix}__pagination--next-title ${cssPrefix}__pagination--title`}>
              {nextTitle}
            </span>
          )}
          <span className={`${cssPrefix}__pagination--next-icon ${cssPrefix}__pagination--icon`}>
            {nextIcon ? <Icon icon={`${cssPrefix}__ico-${nextIcon}`} /> : <span>&#62;</span>}
          </span>
        </Link>
      )}
    </div>
  </div>
);

/**
 * Valid Props
 */
Pagination.propTypes = {
  layout: PropTypes.string,
  nextLabel: PropTypes.string,
  nextTitle: PropTypes.string,
  nextUrl: PropTypes.string,
  nextIcon: PropTypes.string,
  previousLabel: PropTypes.string,
  previousTitle: PropTypes.string,
  previousUrl: PropTypes.string,
  previousIcon: PropTypes.string,
  context: PropTypes.shape(),
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
};

/**
 * Default Props
 */
Pagination.defaultProps = {
  layout: 'default',
  nextLabel: 'Next',
  nextTitle: '',
  nextUrl: '',
  nextIcon: '',
  previousLabel: 'Previous',
  previousTitle: '',
  previousUrl: '',
  previousIcon: '',
  context: {},
  loadStyles: true,
  extraClassNames: {},
};

export default withPolaris(Pagination);
